/**
 * Date Utilities
 *
 * Utility functions for consistent date formatting across the application.
 * All dates should be displayed in DD/MM/YYYY format (26/07/2025).
 */

/**
 * Format date to DD/MM/YYYY format
 * @param {Date|string} date - The date to format
 * @returns {string} Formatted date string in DD/MM/YYYY format
 */
export const formatDateDDMMYYYY = (date) => {
  if (!date) return '';
  
  const dateObj = date instanceof Date ? date : new Date(date);
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) return '';
  
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();
  
  return `${day}/${month}/${year}`;
};

/**
 * Format date to DD/MM/YYYY with time
 * @param {Date|string} date - The date to format
 * @returns {string} Formatted date string in DD/MM/YYYY HH:MM format
 */
export const formatDateTimeDDMMYYYY = (date) => {
  if (!date) return '';
  
  const dateObj = date instanceof Date ? date : new Date(date);
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) return '';
  
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();
  const hours = dateObj.getHours().toString().padStart(2, '0');
  const minutes = dateObj.getMinutes().toString().padStart(2, '0');
  
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

/**
 * Format date with day name in DD/MM/YYYY format
 * @param {Date|string} date - The date to format
 * @returns {string} Formatted date string with day name
 */
export const formatDateWithDayDDMMYYYY = (date) => {
  if (!date) return '';
  
  const dateObj = date instanceof Date ? date : new Date(date);
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) return '';
  
  const dayName = dateObj.toLocaleDateString('en-GB', { weekday: 'short' });
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();
  
  return `${dayName}, ${day}/${month}/${year}`;
};

/**
 * Legacy function name for backwards compatibility
 * @param {Date|string} date - The date to format
 * @returns {string} Formatted date string in DD/MM/YYYY format
 */
export const formatDateDDMMYYYY_Legacy = formatDateDDMMYYYY;
